/**
 * Calendar appointments over MCP — the WRITE half. Deletion lives in
 * `calendarDeletes.js`, beside this file, because it fails differently (there
 * is no undo) and is behind the destructive switch.
 *
 * ## The rules, each of which prevents a specific verified loss
 *
 * 1. **Record-granular, always.** One `extension_records_cas_upsert` per
 *    record. The calendar has no whole-scope writer at all — `saveEvent` is a
 *    single-record upsert and `calendarStore` never calls
 *    `extension_records_sync_scope` — so boards' clobber hazard has no
 *    analogue here. That is a property to keep, not a coincidence: this
 *    directory is forbidden from naming the whole-scope API.
 *
 * 2. **Read-modify-write through the app's OWN `normalizeEvent`.** `data` is a
 *    full replace, and `normalizeEvent`'s header records that a field left out
 *    of its explicit build silently does not survive a save/sync/reload. Build
 *    the record from the arguments and `reminders`, `recurrence`, `exdates`,
 *    `origin` and `created_by` all quietly stop existing on every device.
 *
 * 3. **A scope we cannot confirm, or one the user is a viewer on, is REFUSED
 *    before anything is read** — the shared `refuseUnwritableScope`.
 *
 * 4. **A naive `start` is refused, never coerced.** See `calendarRecords.js`;
 *    this is the headline of the phase.
 *
 * ## Recurrence: `occurrence_date` present = that occurrence, absent = the
 * whole series (**O21**)
 *
 * Structural rather than an extra argument, for three reasons the app itself
 * already settled: it is unambiguous by construction; the DEFAULT is the
 * narrow, recoverable direction (a stray override is visible and fixable,
 * while moving a standing meeting for everyone is not); and it matches the
 * app's own low-ceremony path, where dragging one occurrence is an implicit
 * "this event" with no dialog. `RecurrenceScopeDialog` is a TWO-option dialog —
 * "This event" / "All events" — with *this and following* deliberately
 * deferred and owner-approved, so there is no third mode to invent here.
 *
 * The two write shapes mirror `CalendarView`'s exactly:
 *
 * - **this occurrence** → a NEW record with a fresh uid, `recurrence: null`,
 *   `exdates: []` and `override_of: {master_uid, occurrence_date}`. The master
 *   is NOT touched; `expandEvents` suppresses the generated instance because it
 *   is in the `overridden` set. Forgetting `override_of` produces a duplicate
 *   chip on that day, which only re-expansion reveals.
 * - **the whole series** → rewrite the MASTER, keeping `exdates` and every
 *   existing override record (`applyEditToMaster` keeps both, deliberately).
 *
 * ## Reminders can raise an OS notification, and that is disclosed, not gated
 *
 * `calendarReminderScheduler` is poll-driven: it re-reads `listEvents()` every
 * 30 s and fires anything due, with no per-appointment registration. So a
 * record written here with `reminders: [{minutes_before: 10}]` IS enough — the
 * next scan picks it up. The in-app toast always fires; the OS notification
 * fires when the window is unfocused and the user's own `desktopNotifications`
 * preference is not false. This is the first MCP capability that can make
 * something happen outside Apiboo's window. Nothing is sent to another person,
 * nothing leaves the machine, and the appointment is deletable — so it is not
 * behind the destructive switch — but it is said in three places: the
 * `appointment_create` description, `mcp.capCalendarWriteDesc`, and the
 * Calendar paragraph of `INSTRUCTIONS` (**O22**).
 *
 * ## What is deliberately absent
 *
 * `attendees` — the record has no such field, so this would mean ADDING one; it
 * would sync to the user's other devices and to teammates, render nowhere, and
 * a teammate reading names on an appointment would reasonably believe those
 * people were invited when nothing was sent (**O23**). Invitations in every
 * form. `tz`, `uid`, `color`, `status`, `created_by`, `origin`, `override_of`
 * and `exdates` as arguments. Moving an appointment between calendars (a
 * data-residency change, exactly like `moveBoardToTeam`).
 */
import { invoke } from '@tauri-apps/api/core';
import { expandEvents } from '../../utils/calendarRecurrence';
import { fromDateKey, addDays } from '../../utils/calendarDates';
import { mcpError } from '../errors';
import { capRefusalFor } from '../../plans/capRefusal';
import { countOwnEvents } from '../../extensions/bundled/calendar/calendarStore';
import { PERSONAL } from '../scopes';
import { refuseUnwritableScope, scopeReport } from '../writeScope';
import { revealCalendarWrite } from '../calendarVisibility';
import {
  CALENDAR_EXTENSION_ID,
  checkDateKey,
  checkRecurrence,
  eventOf,
  hostTimezone,
  listCalendarRows,
  newEventUid,
  patchedEvent,
  recordForEvent,
  resolveReminders,
  resolveTimeForm,
  revsForCalendar,
  scopeKeyForCalendar,
  toLocalWallClock,
} from '../calendarRecords';

/** Free-text fields a client may set. Plain text — no sanitizer (§A7.1). */
const TEXT_FIELDS = ['title', 'location', 'description'];

/**
 * Rust answers with `conflict:<rev>` or `too_large:<bytes>:<cap>` and nothing
 * else — a fixed vocabulary, so no underlying error text is ever relayed.
 */
export function fromCalendarRustError(err) {
  const text = typeof err === 'string' ? err : '';
  if (text.startsWith('conflict:')) {
    return mcpError(
      'conflict',
      'That appointment changed since you read it. Read it again and retry with the new rev.',
      { rev: text.slice('conflict:'.length) || null }
    );
  }
  if (text.startsWith('too_large:')) {
    const [, bytes, cap] = text.split(':');
    return mcpError(
      'record_too_large',
      "That appointment is too large to sync to the user's other devices, so NOTHING was written. Retrying will not help — shorten the description.",
      { bytes: Number(bytes) || null, cap: Number(cap) || null }
    );
  }
  return mcpError('write_failed', 'The write could not be completed.');
}

/** One CAS write. `{ rev }` or `{ error }` — never throws for a store error. */
export async function casWriteEvent(scopeKey, record, expectedRev) {
  try {
    const rev = await invoke('extension_records_cas_upsert', {
      workspaceId: scopeKey,
      extensionId: CALENDAR_EXTENSION_ID,
      record,
      expectedRev: expectedRev ?? null,
    });
    return { rev };
  } catch (err) {
    return { error: fromCalendarRustError(err) };
  }
}

/**
 * Everything a write needs before it touches the store: the resolved scope key,
 * or the refusal. Rules 3 and 4 of `writeScope.js` both live here, so no
 * handler can forget one.
 */
export function targetCalendarScope(wire) {
  const refusal = refuseUnwritableScope(wire);
  if (refusal) return { error: refusal };
  return { scopeKey: scopeKeyForCalendar(wire) };
}

/** Every event of one scope plus its revs, for a read-modify-write. */
export async function loadForWrite(scopeKey) {
  const rows = await listCalendarRows(scopeKey);
  const events = rows.map(eventOf).filter(Boolean);
  const revs = await revsForCalendar(scopeKey);
  return { events, revs };
}

/**
 * The text + reminders + recurrence half of a write, validated once so create
 * and update cannot disagree about what is legal.
 */
export function collectFields(args) {
  const patch = {};
  for (const field of TEXT_FIELDS) {
    if (args[field] === undefined) continue;
    if (typeof args[field] !== 'string') {
      return { error: mcpError('invalid_argument', `${field} must be text.`, { field }) };
    }
    patch[field] = args[field];
  }
  const reminders = resolveReminders(args.reminders);
  if (reminders.error) return { error: reminders.error };
  if (reminders.reminders !== undefined) patch.reminders = reminders.reminders;

  if (args.recurrence !== undefined) {
    const bad = checkRecurrence(args.recurrence);
    if (bad) return { error: bad };
    patch.recurrence = args.recurrence ?? null;
  }
  return { patch };
}

/** The result shape every write tool returns, so a client never special-cases. */
function writeResult(event, { wire, rev, occurrenceDate = null, mode, overrideRecordId = null }) {
  return {
    appointmentId: occurrenceDate ? event.override_of?.master_uid || event.uid : event.uid,
    occurrenceDate,
    overrideRecordId,
    mode,
    title: event.title || '',
    allDay: event.all_day === true,
    start: event.all_day ? null : event.start || null,
    end: event.all_day ? null : event.end || null,
    startLocal: event.all_day ? null : toLocalWallClock(event.start),
    endLocal: event.all_day ? null : toLocalWallClock(event.end),
    startDate: event.all_day ? event.start_date || null : null,
    endDate: event.all_day ? event.end_date || null : null,
    recurring: !!event.recurrence,
    reminders: (Array.isArray(event.reminders) ? event.reminders : []).map(
      (r) => r.minutes_before
    ),
    scope: wire,
    rev: rev ?? null,
    timezone: hostTimezone(),
    ...revealCalendarWrite(event),
    ...scopeReport(wire),
  };
}

// ── appointment_create ─────────────────────────────────────────────────────

export async function appointmentCreate(args) {
  const wire = args.scope || PERSONAL;
  const target = targetCalendarScope(wire);
  if (target.error) return target.error;

  if (typeof args.title !== 'string' || args.title.trim() === '') {
    return mcpError('invalid_argument', 'title is required.', { field: 'title' });
  }

  // Plans v2: a personal appointment counts against the plan's local cap.
  if (wire === PERSONAL) {
    const capped = await capRefusalFor('calendarEvents', countOwnEvents);
    if (capped) return mcpError('plan_limit', capped, { requiredPlan: 'basic' });
  }

  const times = resolveTimeForm(args);
  if (times.error) return times.error;
  if (!times.times) {
    return mcpError(
      'invalid_argument',
      "An appointment needs a time. Send start/end with Z or an offset, start_local/end_local as the user's wall clock, or all_day with start_date.",
      { field: 'start' }
    );
  }

  const fields = collectFields(args);
  if (fields.error) return fields.error;

  // The id is minted HERE, never accepted from the client: an agent choosing an
  // id could overwrite an existing appointment. `patchedEvent` then runs the
  // app's own `normalizeEvent` over the whole thing, so the record is complete
  // by construction rather than by this file remembering every field.
  const event = patchedEvent(
    {
      uid: newEventUid(),
      calendar: wire,
      tz: undefined,
      created_at: undefined,
    },
    { ...fields.patch, ...times.times }
  );

  // No `expected_rev`: `cas_upsert_in` answers `conflict:` for an absent row
  // whenever one is PRESENT, so a create must not pass one.
  const written = await casWriteEvent(target.scopeKey, recordForEvent(event), null);
  if (written.error) return written.error;

  return writeResult(event, { wire, rev: written.rev, mode: 'single' });
}

// ── appointment_update ─────────────────────────────────────────────────────

export async function appointmentUpdate(args) {
  const wire = args.scope || PERSONAL;
  const target = targetCalendarScope(wire);
  if (target.error) return target.error;

  if (!args.expected_rev) {
    return mcpError(
      'expected_rev_required',
      'Changing something a person may have typed requires expected_rev from a preceding appointments_list or appointment_read.'
    );
  }
  if (args.occurrence_date !== undefined && args.occurrence_date !== null) {
    const bad = checkDateKey(args.occurrence_date, 'occurrence_date');
    if (bad) return bad;
  }

  const times = resolveTimeForm(args);
  if (times.error) return times.error;
  const fields = collectFields(args);
  if (fields.error) return fields.error;
  const patch = { ...fields.patch, ...(times.times || {}) };
  if (Object.keys(patch).length === 0) {
    return mcpError('invalid_argument', 'Nothing to change.', { field: 'title' });
  }

  const { events, revs } = await loadForWrite(target.scopeKey);
  const id = String(args.appointment_id);
  const master = events.find((e) => e.uid === id);
  if (!master) {
    return mcpError('not_found', 'No appointment with that id in this scope.');
  }

  // ── the whole series, or a single event ──────────────────────────────────
  if (!args.occurrence_date) {
    if (revs.get(master.uid) !== args.expected_rev) {
      return mcpError(
        'conflict',
        'That appointment changed since you read it. Read it again and retry with the new rev.',
        { rev: revs.get(master.uid) ?? null }
      );
    }
    // `exdates` and the existing override records are KEPT — deliberately, as
    // `applyEditToMaster` keeps them. A series edit is not a series reset.
    const next = patchedEvent(master, patch);
    const written = await casWriteEvent(
      target.scopeKey,
      recordForEvent(next),
      args.expected_rev
    );
    if (written.error) return written.error;
    return writeResult(next, {
      wire,
      rev: written.rev,
      mode: master.recurrence ? 'series' : 'single',
    });
  }

  // ── one occurrence ───────────────────────────────────────────────────────
  if (!master.recurrence) {
    return mcpError(
      'invalid_parent',
      'That appointment does not repeat, so it has no occurrences. Update it without occurrence_date.',
      { appointmentId: master.uid }
    );
  }
  const occurrenceDate = String(args.occurrence_date);
  if ((master.exdates || []).includes(occurrenceDate)) {
    return mcpError(
      'not_found',
      'That occurrence has been cancelled, so there is nothing to change on it.',
      { appointmentId: master.uid, occurrenceDate }
    );
  }

  const existing = events.find(
    (e) =>
      e.override_of?.master_uid === master.uid &&
      e.override_of?.occurrence_date === occurrenceDate
  );

  if (existing) {
    // Already materialized: the OVERRIDE record is the CAS target.
    if (revs.get(existing.uid) !== args.expected_rev) {
      return mcpError(
        'conflict',
        'That occurrence changed since you read it. Read it again and retry with the new rev.',
        { rev: revs.get(existing.uid) ?? null }
      );
    }
    const next = patchedEvent(existing, patch);
    const written = await casWriteEvent(
      target.scopeKey,
      recordForEvent(next),
      args.expected_rev
    );
    if (written.error) return written.error;
    return writeResult(next, {
      wire,
      rev: written.rev,
      occurrenceDate,
      mode: 'occurrence',
      overrideRecordId: next.uid,
    });
  }

  // Not yet materialized. The MASTER's rev is the guard: an agent working from
  // a stale read (the rule changed, this occurrence no longer exists) is
  // refused rather than writing an override that suppresses nothing.
  if (revs.get(master.uid) !== args.expected_rev) {
    return mcpError(
      'conflict',
      'The series changed since you read it. Read it again and retry with the new rev.',
      { rev: revs.get(master.uid) ?? null }
    );
  }
  const instance = occurrenceInstance(master, occurrenceDate);
  if (!instance) {
    return mcpError(
      'not_found',
      'That series has no occurrence on that date, so there is nothing to change.',
      { appointmentId: master.uid, occurrenceDate }
    );
  }

  // A NEW record with a fresh uid. `recurrence: null` and `exdates: []` because
  // an exception is a single event; `override_of` is what makes expansion
  // suppress the generated instance instead of rendering both.
  const override = patchedEvent(
    {
      ...instance,
      uid: newEventUid(),
      tz: undefined,
      created_at: undefined,
      calendar: master.calendar,
    },
    {
      ...patch,
      recurrence: null,
      exdates: [],
      override_of: { master_uid: master.uid, occurrence_date: occurrenceDate },
    }
  );
  const written = await casWriteEvent(target.scopeKey, recordForEvent(override), null);
  if (written.error) return written.error;
  return writeResult(override, {
    wire,
    rev: written.rev,
    occurrenceDate,
    mode: 'occurrence',
    overrideRecordId: override.uid,
  });
}

/**
 * The concrete instance a series produces on one local date, or null.
 *
 * Uses the app's own expansion over a ONE-DAY window rather than re-deriving
 * the rule, so "does the rule generate that day" is answered by the same code
 * the grid renders from — including the monthly skip rule, INTERVAL anchoring
 * and the UNTIL/COUNT stops.
 */
function occurrenceInstance(master, occurrenceDate) {
  const day = fromDateKey(occurrenceDate);
  if (!day) return null;
  const instances = expandEvents([master], day, addDays(day, 1));
  const hit = instances.find((i) => i.occurrence_date === occurrenceDate);
  if (!hit) return null;
  const { occurrence_date: _od, is_occurrence: _io, ...clean } = hit;
  return clean;
}
